// src/hooks/useReport.ts
// Hook pour gérer les signalements de contenu
import { useMutation } from '@tanstack/react-query';
import api from '../services/api';
import { ReportReason } from '../components/common/ReportDialog';

interface ReportParams {
  targetType: 'post' | 'comment' | 'user';
  targetId: number;
  reason: ReportReason;
  details?: string;
}

interface ReportResponse {
  id: number;
  status: string;
  message: string;
}

export const useReport = () => {
  return useMutation({
    mutationFn: async ({ targetType, targetId, reason, details }: ReportParams): Promise<ReportResponse> => {
      const endpoint = `/reports/`;

      const payload = {
        target_type: targetType,
        target_id: targetId,
        reason,
        details,
      };

      const response = await api.post(endpoint, payload);
      return response.data;
    },
  });
};

// Hook pour récupérer les signalements (admin)
export const useReports = (status?: 'pending' | 'reviewed' | 'resolved') => {
  // Peut être étendu avec useQuery si nécessaire
  return {
    fetchReports: async (page = 0, limit = 20) => {
      const params: Record<string, unknown> = { offset: page * limit, limit };
      if (status) params.status = status;

      const response = await api.get('/reports/', { params });
      return response.data;
    },
  };
};

// Hook pour résoudre un signalement (admin)
export const useResolveReport = () => {
  return useMutation({
    mutationFn: async ({
      reportId,
      resolution,
      action,
    }: {
      reportId: number;
      resolution: 'dismissed' | 'warning' | 'content_removed' | 'user_banned';
      action?: string;
    }) => {
      const response = await api.post(`/reports/${reportId}/resolve`, {
        resolution,
        action,
      });
      return response.data;
    },
  });
};

export default useReport;
